// =============================================================================
// DESIGN TOKENS
// =============================================================================
// Structure
//   primitive         — raw scale values; spacing, sizing, typography, shadows,
//                       gradients, radii. Use these directly in styled components.
//   semantic.color    — named color aliases over the primitive palette.
//                       Exists so a brand color change is a one-line edit,
//                       not a grep across 40 files.
//
// Spacing scale
//   Keys are the pixel value on a 4px grid (space[8] = '8px').
//   (*) = intentional off-grid value from Figma spec — do not change without
//   design sign-off. Layout-positioning values live in primitive.space.layout.
//
// Adding new tokens
//   New spacing     → add primitive.space[N], use directly in your component
//   New color       → add to primitive.color, alias in semantic.color if needed
//   New text style  → add to primitive.typography scale, compose in component
//   New design file → slot new values into existing scale categories
// =============================================================================

// ---------------------------------------------------------------------------
// PRIMITIVE
// ---------------------------------------------------------------------------
const PRIMARY_COLOR = 'var(--brand-primary, #403A60)';
const SECONDARY_COLOR = 'var(--brand-secondary, #C4AA6F)';

const primitive = {
  // -------------------------------------------------------------------------
  // COLOR PALETTE
  // Raw named colours. No context, no opinion.
  // -------------------------------------------------------------------------
  color: {
    red: '#E60E0E',
    // Brand
    purple: PRIMARY_COLOR,
    gold: SECONDARY_COLOR,
    white: '#FFFFFF',
    black: '#000000',
    offWhite: '#FEFCF9', // news item card background
    charcoal: '#212123', // section heading text
    ink: '#1C1C1C', // news item title text
    // Greys
    grey100: '#F0F0F0', // room info background
    grey200: '#EFEFEF', // chip / tag background
    grey300: '#C8C8C8', // desktop canvas background
    grey500: '#6F6F6F', // date / description meta text
    // Translucent whites
    whiteAlpha4: 'rgba(255, 255, 255, 0.04)', // body margin peek
    whiteAlpha10: '#FFFFFF1A',
    whiteAlpha30: 'rgba(255, 255, 255, 0.30)', // button top border
    whiteAlpha54: 'rgba(245, 245, 245, 0.54)', // unselected category pill
    whiteAlpha60: 'rgba(245, 245, 245, 0.6)',
    whiteAlpha94: 'rgba(255, 255, 255, 0.94)', // topbar
    whiteAlpha96: '#FFFFFFF0', // header / dialog body text
    whiteAlpha93: '#FFFFFFED', // extension card background
    whiteAlpha70: 'rgba(255, 255, 255, 0.70)', // about-hotel card + stat chip background
    whiteAlpha75: '#FFFFFFBF', // room services card background

    // Delivery card backgrounds
    greenAlpha68: 'rgba(229, 255, 221, 0.68)', // pickup card
    blueAlpha68: 'rgba(221, 250, 255, 0.68)', // standard delivery
    amberAlpha68: 'rgba(255, 241, 195, 0.68)', // express delivery

    // Delivery card borders
    green: '#87ED82',
    cyan: '#00D0FF',



    // -----------------------------------------------------------------------
    // TV / STREAM UI
    // -----------------------------------------------------------------------
    // shades of primary and seconday 
    // lighter
    primary300: `color-mix(in srgb, ${PRIMARY_COLOR} 80%, white)`,
    // darker
    primary700: `color-mix(in srgb, ${PRIMARY_COLOR} 60%, black)`,
    //glassy primary and secondary
    glassy: `color-mix(in srgb, color-mix(in srgb, ${SECONDARY_COLOR} 30%, #FFF) 20%, black)`,
    glassyPrimary: `color-mix(in srgb, color-mix(in srgb, ${PRIMARY_COLOR} 15%, transparent) 60%, transparent)`,
    glassyTransparent: `color-mix(in srgb, ${SECONDARY_COLOR} 15%, #2C2C2EB6)`,
    whitePrimary10: `color-mix(in srgb, ${PRIMARY_COLOR} 90%, white 10%)`,
  },

  // ------------------------------------------------------ -------------------
  // SPACE SCALE  (4px grid)
  // Key = pixel value. To add a value: space[N] = 'Npx' — done.
  // (*) = off-grid, intentional from Figma spec.
  // -------------------------------------------------------------------------
  space: {
    1: '1px',
    2: '2px',
    3: '3px',
    4: '4px',
    5: '5px',
    6: '6px',
    8: '8px',
    9: '9px',
    10: '10px', // (*) sub-menu / nav padding
    11: '11px',
    12: '12px',
    13: '13px',
    14: '14px', // (*) clock-to-dining-hours gap
    15: '15px', // (*) gap between news item cards
    16: '16px',
    18: '18px',
    20: '20px',
    23: '23px', // (*) settings column gap
    24: '24px',
    25: '25px', // (*) between multi-section page menu items
    26: '26px', // (*) important-notes list top margin
    28: '28px',
    30: '30px', // (*) sub-menu / back-button horizontal padding
    32: '32px',
    35: '35px', // (*) prices card title to list gap
    37: '37px', // (*) prices card title to list gap
    38: '38px', // (*) price item name to price gap
    40: '40px',
    41: '41px',
    44: '44px', // manager card row gap
    48: '48px',
    50: '50px',
    52: '52px', // gap between hotel overview and amenities section // (*) icon-to-welcome gap, weather-to-room-info gap
    56: '56px',
    60: '60px', // (*) main layout top margin
    62: '62px', // (*) extension card to delivery section gap
    64: '64px',
    66: '66px', // (*) channel loading spinner arc padding (vertical)
    80: '80px',
    96: '96px',
    109: '109px', // (*) weather card icon box size
    112: '112px',
    128: '128px',
    136: '136px', // (*) channel loading spinner arc padding (horizontal)
    160: '160px',
    186: '186px',
    192: '192px',
    310: '310px', // (*) settings language icon size
  },

  // -------------------------------------------------------------------------
  // TYPOGRAPHY SCALE
  // -------------------------------------------------------------------------
  typography: {
    fontFamily: {
      base: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },

    fontWeight: {
      regular: '400',
      medium: '500',
      semiBold: '600',
      bold: '700',
      extraBold: '800',
    },

    // Key = px value for easy lookup
    fontSize: {
      11: '11px',
      14: '14px', // caption
      16: '16px', // detail label
      17: '17px', // meta / subtitle
      24: '24px', // section / page heading
      18: '18px', // button label  (was 17.5 — rounded)
      19: '19px', // subtext / component label
      20: '20px', // body standard / channel names
      21: '21px', // body mid
      22: '22px', // settings list item
      23: '23px', // stat numbers in about/info pages
      25: '25px', // body large
      26: '26px', // body large / heading small
      30: '30px', // section heading
      31: '31px', // section heading alt
      37: '37px', // display medium
      39: '39px',
      40: '40px', // display / hero
    },

    lineHeight: {
      tight: '100%', // most text styles
      normal: '120%', // button labels
      relaxed: '150%', // section headings
      fixed28: '28px', // heading-30 fixed variant
      fixed29: '29px', // body-25 fixed variant
    },

    letterSpacing: {
      none: '0px',
      button: '0.29px',
    },
  },

  // -------------------------------------------------------------------------
  // BORDER RADIUS
  // Key = px value for easy lookup
  // -------------------------------------------------------------------------
  radius: {
    3: '3px',
    8: '8px', // logo container thumbnail
    10: '10px', // room info widget
    12: '12px', // news item card
    13: '13px', // channel row card
    14: '14px', // TV page pills
    15: '15px',
    18: '18px',
    21: '21px', // standard — buttons, content cards, delivery cards
    24: '24px', // channel loading overlay card
    25: '25px', // large card variant
    28: '28px', // scroll indicator
    39: '39px', // main screen card / content frame
    66: '66px', // sub-menu pill / bottom nav pill
    full: '9999px', // fully circular
  },

  // -------------------------------------------------------------------------
  // SHADOWS
  // -------------------------------------------------------------------------
  shadow: {
    sm: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', // cards, hotel picture
    md: '0px 8px 16px -3px rgba(0, 0, 0, 0.12), 0px 0px 1.5px 0px rgba(0, 0, 0, 0.20)', // menu items
    lg: '0px 10px 21px -3.56px rgba(0, 0, 0, 0.12), 0px 0px 2px 0px rgba(0, 0, 0, 0.20)', // content card
    overlay: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // channel loading overlay card
    subtle: '0px 0px 2px 0px rgba(0, 0, 0, 0.20)', // extension card / stat chip / manager card
    amenityCard: '0px 0px 1px 0px rgba(0, 0, 0, 0.20), 0px 7px 14px -2.5px rgba(0, 0, 0, 0.12)', // amenities service cards
    focusRing: `0 0 0 3px ${SECONDARY_COLOR}`, // gold ring — TV remote focus indicator
    welcomeButtonFocus: `0 6px 20px color-mix(in srgb, ${SECONDARY_COLOR} 40%, transparent)`,
    //NEW DESIGN TV : WARWICK
    white: '0 0 20px #E1E7E9D0, 0 0 0.1px #DADFE08E',
    primary: `0 0 18px ${PRIMARY_COLOR}, 0 0 35px #000`,
    secondary: `0 0 18px ${SECONDARY_COLOR}, 0 0 35px #000`,

  },

  // -------------------------------------------------------------------------
  // SIZING — fixed component / screen dimensions
  // -------------------------------------------------------------------------
  size: {
    icon: {
      sm: '17px',
      md: '20px', // was 19.36 — rounded
      lg: '40px',
      xlg: '52px',
    },
    radio:{
      radioCardWidth: '300px',
      radioCardHeight: '300px',
    },
    tv: {
      sidebarWidth: '400px',

      channelCard: '180px',
      

      sidebarLogo: {
        width: '60px',
        height: '36px',
      },

      channelNumberWidth: '46px',

      cardLogo: {
        width: '128px',
        height: '64px',
      },

      signal: {
        width: '12px',
        height: '12px',
        barWidth: '3px',
      },

      spinner: '18px',

      loadingTextMaxWidth: '200px',
    },
    newsThumb: {
      width: '243px',
      height: '128px',
    },
    newsSourceCard: {
      width: '191px',
      height: '168px',
    },
  },
};

// ---------------------------------------------------------------------------
// SEMANTIC COLOR
// Named aliases over primitive.color only.
// One place to update if a brand colour changes hex.
// ---------------------------------------------------------------------------
const semantic = {
  color: {
    background: {
      screen: primitive.color.white,
      canvas: primitive.color.grey300,
      header: primitive.color.whiteAlpha96,
      topbar: primitive.color.whiteAlpha94,
      sidebar: primitive.color.purple,
      nav: primitive.color.purple,
      subMenu: primitive.color.purple,
      bodyMargin: primitive.color.whiteAlpha4,
      roomInfo: primitive.color.grey100,
      extensionCard: primitive.color.whiteAlpha93,
      roomServicesCard: primitive.color.whiteAlpha75,
      logoContainer: primitive.color.white,
      chip: primitive.color.grey200,
      rowDefault: primitive.color.purple,
      rowActive: primitive.color.gold,
      categoryDefault: primitive.color.whiteAlpha54,
      welcomeDialog: primitive.color.purple,
      welcomeScreen: `linear-gradient(135deg, color-mix(in srgb, ${PRIMARY_COLOR} 88%, black) 0%, color-mix(in srgb, ${PRIMARY_COLOR} 68%, black) 50%, color-mix(in srgb, ${PRIMARY_COLOR} 88%, black) 100%)`,
      welcomeModalOverlay: `color-mix(in srgb, ${PRIMARY_COLOR} 65%, transparent)`,
      cleaningDaily: primitive.color.gold,
      cleaningLastCleaned: primitive.color.purple,
      bottomMenuDefault: primitive.color.purple,
      bottomMenuActive: primitive.color.gold,
      priceList: primitive.color.purple,
      pickupCard: primitive.color.greenAlpha68,
      standardDelivery: primitive.color.blueAlpha68,
      expressDelivery: primitive.color.amberAlpha68,
      glassy: primitive.color.glassy,
      glassyTransparent: primitive.color.glassyTransparent
    },

    text: {
      notification: primitive.color.red,
      onDark: primitive.color.white,
      onLight: primitive.color.purple,
      dialogAccent: primitive.color.gold,
      dialogBody: primitive.color.whiteAlpha96,
      weatherTime: primitive.color.purple,
      priceHeading: primitive.color.gold,
      priceItem: primitive.color.white,
    },

    button: {
      backgroundPrimary: primitive.color.gold,
      backgroundSecondary: primitive.color.purple,
      borderPrimary: primitive.color.whiteAlpha30,
      borderSecondary: primitive.color.white,
      label: primitive.color.white,
    },

    icon: {
      default: primitive.color.white,
      accent: primitive.color.gold,
      selectedBg: primitive.color.gold,
    },

    border: {
      backButton: primitive.color.white,
      pickupCard: primitive.color.green,
      standardDelivery: primitive.color.cyan,
      expressDelivery: primitive.color.gold,
      managerSignature: primitive.color.gold,
      continueButton: primitive.color.whiteAlpha30,
    },
  },
  // -------------------------------------------------------------------------
  // GRADIENTS
  // -------------------------------------------------------------------------
  gradient: {
    hotelPicture:
      'linear-gradient(180.03deg, rgba(191, 191, 191, 0.25) 0.03%, rgba(191, 191, 191, 0.25) 52.76%, rgba(42, 42, 42, 0.25) 82.19%)',
    mainSelectedCard:
      'linear-gradient(180deg, rgba(115, 115, 115, 0) 50.06%, rgba(26, 26, 26, 0.54) 66.04%)',
    mainUnselectedCard:
      'linear-gradient(180.03deg, rgba(191, 191, 191, 0.40) 0.03%, rgba(191, 191, 191, 0.54) 52.76%, rgba(42, 42, 42, 0.54) 82.19%)',
    roomServicesImage:
      'linear-gradient(179.92deg, rgba(144, 144, 144, 0) 18.44%, rgba(42, 42, 42, 0.48) 73.29%)',
    primaryToBlack: `linear-gradient(90deg, color-mix(in srgb, color-mix(in srgb, ${PRIMARY_COLOR} 20%, #FFF) 15%, transparent) 80%, #00000044 70%)`,
    secondaryToBlack: `linear-gradient(90deg, color-mix(in srgb, color-mix(in srgb, ${SECONDARY_COLOR} 20%, #FFF) 15%, transparent) 80%, #00000044 70%)`,
    // Inside semantic.gradient:
    ringGlow: `radial-gradient(
    circle at 50% 45%,
    color-mix(in srgb, ${PRIMARY_COLOR} 20%, transparent) 45%,
    color-mix(in srgb, ${PRIMARY_COLOR} 8%, transparent) 65%,
  #080604 85%
) `, //inside semantic.gradient: spotlightGlow
  },
  // -------------------------------------------------------------------------
  // SIZING — fixed component / screen dimensions
  // -------------------------------------------------------------------------
  size: {
    appIcon: {
      width: '175px',
      height: '78px',
    },
    nav: {
      iconSize: '30px',
      buttonSize: '40px',
      buttonScaleActive: '1.1',
      avatar: '50px',
      badge: '10px',
    },
    button: {
      widthSm: '102px',
      widthMd: '193px', // was 192.73 — rounded
      height: '54px', // was 53.67  — rounded
    },
    weatherIcon: '46px',
    clockIcon: '22px',
    phoneIcon: '46px',
    menuItemIcon: { width: '82px', height: '99px' },
    menuItem: {
      minHeight: '300px',
      width: '33.333%',
      scaleActive: 1.1,
    },
  },
};

export const tokens = { primitive, semantic };

export function applyBrandColors({ primary_color, secondary_color } = {}) {
  const root = document.documentElement;
  if (primary_color) root.style.setProperty('--brand-primary', primary_color);
  if (secondary_color) root.style.setProperty('--brand-secondary', secondary_color);
}
